import React, { memo, useState } from 'react'
import PersonUi from '../PersonUi.tsx'
import { PresentCandidateValues } from '../../api/role/models/CandidateDto.ts'
import Section from '../Section.tsx'
import PersonSmallPic from '../PersonSmallPic.tsx'
import InterviewForThisRolePill from '../InterviewForThisRolePill.tsx'
import { formatDuration } from '../../utils/dateUtils.ts'
import LoadingSpinner from '../LoadingSpinner.tsx'
import RankingScore from '../RankingScore.tsx'
import { toast } from 'sonner'
import MultiStateSelector from '../MultiStateSelector.tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCheck,
    faListCheck,
    faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { safeCastToEnum } from '../../utils/enumUtils.ts'
import { useSafeUserData } from '../../stateStores/User.ts'
import { AuthHelper } from '../../auth/AuthHelper.ts'
import { UserRoles } from '../../api/tenant/models/UserDto.ts'
import LoadingSpinnerOverlay from '../LoadingSpinnerOverlay.tsx'
import { InterviewForThisRoleValues } from '../../api/role/models/CandidateConnectionDto.ts'
import { FeedbackValues } from '../../api/role/models/FeedbackDto.ts'
import CompanyUi from '../CompanyUi.tsx'
import { useManageRoleStore } from './stateStore/manageRoleStore.ts'
import TriStateToggle from '../TriStateToggle.tsx'

function CandidateListComponent(): React.JSX.Element {
    // IMPORTANT!!!!!
    // For absolutely no reason at all, we MUST subscribe to the state one fucking thing at a time
    //  if we don't, we get all sorts of infinite loops and extra re-renders and all sorts of shit.
    const candidates = useManageRoleStore((state) => state.candidates)
    const personLiteCache = useManageRoleStore().getState().personLiteCache
    const companyCache = useManageRoleStore().getState().companyCache
    const fetchingCandidates = useManageRoleStore(
        (state) => state.fetchingCandidates,
    )
    const presentLoadingByCandidateId = useManageRoleStore(
        (state) => state.presentLoadingByCandidateId,
    )
    const feedbackLoadingByCandidateId = useManageRoleStore(
        (state) => state.feedbackLoadingByCandidateId,
    )
    const hasMoreCandidates = useManageRoleStore(
        (state) => state.hasMoreCandidates,
    )
    const updatePresentForCandidate = useManageRoleStore(
        (state) => state.updatePresentForCandidate,
    )
    const updateFeedbackValueForCandidate = useManageRoleStore(
        (state) => state.updateFeedbackValueForCandidate,
    )
    const localUpdateFeedbackComment = useManageRoleStore(
        (state) => state.localUpdateFeedbackComment,
    )
    const updateFeedbackCommentForCandidate = useManageRoleStore(
        (state) => state.updateFeedbackCommentForCandidate,
    )
    const updateInterviewForCandidate = useManageRoleStore(
        (state) => state.updateInterviewForCandidate,
    )
    const fetchNextCandidates = useManageRoleStore(
        (state) => state.fetchNextCandidates,
    )
    const selectCandidateId = useManageRoleStore(
        (state) => state.selectCandidateId,
    )
    const setRightPaneMode = useManageRoleStore(
        (state) => state.setRightPaneMode,
    )
    // IMPORTANT!!!!!

    const { user } = useSafeUserData()

    const [visibleConnectionsByCandidate, setVisibleConnectionsByCandidate] =
        useState<Record<string, number>>({})

    if (fetchingCandidates && candidates.length === 0) {
        return (
            <div>
                <LoadingSpinner show={fetchingCandidates} />
            </div>
        )
    }

    const notSelectedValue = 'not_selected'

    const feedbackOptionDetails: Record<
        FeedbackValues | 'not_selected',
        {
            displayValue: string
            backgroundColor: string
            fontColor: string
            display: string
            icon?: JSX.Element
        }
    > = {
        not_selected: {
            displayValue: 'Not Selected',
            backgroundColor: '#5D6D7E',
            fontColor: '#fff',
            display: 'none',
            icon: <FontAwesomeIcon icon={faTimes} />,
        },
        [FeedbackValues.WILL_WORK_WITH_AGAIN]: {
            displayValue: '',
            backgroundColor: '#2ECC71',
            fontColor: '#fff',
            display: 'block',
            icon: <FontAwesomeIcon icon={faCheck} />,
        },
        [FeedbackValues.WILL_NOT_WORK_WITH_AGAIN]: {
            displayValue: '',
            backgroundColor: '#E74C3C',
            fontColor: '#fff',
            display: 'block',
            icon: <FontAwesomeIcon icon={faTimes} />,
        },
        [FeedbackValues.MIGHT_WORK_WITH_AGAIN]: {
            displayValue: 'Maybe',
            backgroundColor: '#F39C12',
            fontColor: '#fff',
            display: 'block',
        },
        [FeedbackValues.DO_NOT_KNOW_THEM]: {
            displayValue: 'Unfamiliar',
            backgroundColor: '#5D6D7E',
            fontColor: '#fff',
            display: 'block',
        },
        [FeedbackValues.HIDDEN]: {
            displayValue: 'Hidden',
            backgroundColor: '#5D6D7E',
            fontColor: '#fff',
            display: 'none',
        },
    }

    const structuredFeedbackOptions = [
        [
            {
                value: FeedbackValues.WILL_WORK_WITH_AGAIN,
                ...feedbackOptionDetails[FeedbackValues.WILL_WORK_WITH_AGAIN],
            },
            {
                value: FeedbackValues.WILL_NOT_WORK_WITH_AGAIN,
                ...feedbackOptionDetails[
                    FeedbackValues.WILL_NOT_WORK_WITH_AGAIN
                ],
            },
            {
                value: FeedbackValues.MIGHT_WORK_WITH_AGAIN,
                ...feedbackOptionDetails[FeedbackValues.MIGHT_WORK_WITH_AGAIN],
            },
            {
                value: notSelectedValue,
                ...feedbackOptionDetails.not_selected,
            },
        ],
        [
            {
                value: FeedbackValues.DO_NOT_KNOW_THEM,
                ...feedbackOptionDetails[FeedbackValues.DO_NOT_KNOW_THEM],
            },
        ],
    ]

    const interviewOptionDetails: Record<
        InterviewForThisRoleValues | 'not_selected',
        {
            displayValue: string
            backgroundColor: string
            fontColor: string
            display: string
            icon: JSX.Element
        }
    > = {
        not_selected: {
            displayValue: 'Not Selected',
            backgroundColor: '#5D6D7E',
            fontColor: '#fff',
            display: 'none',
            icon: <FontAwesomeIcon icon={faTimes} />,
        },
        [InterviewForThisRoleValues.YES]: {
            displayValue: '',
            backgroundColor: '#2ECC71',
            fontColor: '#fff',
            display: 'block',
            icon: <FontAwesomeIcon icon={faCheck} />,
        },
        [InterviewForThisRoleValues.NO]: {
            displayValue: '',
            backgroundColor: '#E74C3C',
            fontColor: '#fff',
            display: 'block',
            icon: <FontAwesomeIcon icon={faTimes} />,
        },
    }

    const structuredInterviewOptions = [
        [
            {
                value: PresentCandidateValues.YES,
                ...interviewOptionDetails[PresentCandidateValues.YES],
            },
            {
                value: PresentCandidateValues.NO,
                ...interviewOptionDetails[PresentCandidateValues.NO],
            },
            {
                value: notSelectedValue,
                ...interviewOptionDetails.not_selected,
            },
        ],
    ]

    async function handlePresentCandidateValueChange(
        personId: string,
        value: string,
    ) {
        try {
            await updatePresentForCandidate(
                personId,
                value === 'not_selected'
                    ? undefined
                    : safeCastToEnum(value, PresentCandidateValues),
            )
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem updating the candidate, please try again.`,
                {
                    duration: 5000,
                },
            )
        }
    }

    async function saveCommentChanges(candidateId: string) {
        try {
            await updateFeedbackCommentForCandidate(candidateId)
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem updating the candidate feedback comment, please try again.`,
                {
                    duration: 5000,
                },
            )
        }
    }

    async function handleInterviewChange(
        candidateId: string,
        newValue?: InterviewForThisRoleValues,
    ) {
        try {
            await updateInterviewForCandidate(candidateId, newValue)
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem updating the candidate interview status, please try again.`,
                {
                    duration: 5000,
                },
            )
        }
    }

    async function showMoreClicked() {
        await fetchNextCandidates()
    }

    return (
        <div className="main-container flex flex-col mt-4">
            {candidates.map((candidate, index) => {
                const candidatePerson = personLiteCache.getOrThrow(
                    candidate.candidatePersonId,
                )

                // Determine how many connections to show for this candidate.
                const candidateId = candidate.candidatePersonId
                const visibleConnections =
                    visibleConnectionsByCandidate[candidateId] || 2
                const connectionsToShow = candidate.connections.slice(
                    0,
                    visibleConnections,
                )

                const hiringManagerConnection = candidate.connections.find(
                    (connection) =>
                        connection.connectedPersonId === user.personId,
                )

                return (
                    <div key={`candidate-${index}`} className="w-full">
                        <hr className="my-6"></hr>
                        <div className="flex gap-4 mb-6">
                            <PersonUi
                                person={candidatePerson}
                                onSelect={(person) => {
                                    selectCandidateId(person.id)
                                    setRightPaneMode('candidate')
                                }}
                            />
                            <div className="flex flex-col">
                                {AuthHelper.userHasRole(user, UserRoles.TA) && (
                                    <div className="mb-4">
                                        <LoadingSpinnerOverlay
                                            show={
                                                presentLoadingByCandidateId.get(
                                                    candidate.candidatePersonId,
                                                ) ?? false
                                            }
                                            className="min-w-[100px]"
                                        >
                                            <div className="text-sm mb-2 text-gray-700">
                                                <FontAwesomeIcon
                                                    icon={faListCheck}
                                                    className="mr-2"
                                                />
                                                <span className="font-bold">
                                                    Shortlisted
                                                </span>
                                            </div>
                                            <TriStateToggle
                                                value={
                                                    candidate.present ??
                                                    notSelectedValue
                                                }
                                                onChange={(newValue) =>
                                                    handlePresentCandidateValueChange(
                                                        candidate.candidatePersonId,
                                                        newValue,
                                                    )
                                                }
                                            />
                                        </LoadingSpinnerOverlay>
                                    </div>
                                )}
                                {!AuthHelper.userHasRole(
                                    user,
                                    UserRoles.TA,
                                ) && (
                                    <div className="field-label">
                                        <LoadingSpinnerOverlay
                                            show={
                                                feedbackLoadingByCandidateId.get(
                                                    candidate.candidatePersonId,
                                                ) ?? false
                                            }
                                            className="min-w-[100px]"
                                        >
                                            <div className="flex space-x-4">
                                                <Section
                                                    title="Work with again?"
                                                    titleType="h2"
                                                >
                                                    <MultiStateSelector
                                                        options={
                                                            structuredFeedbackOptions
                                                        }
                                                        selectedValue={
                                                            hiringManagerConnection
                                                                ?.feedback
                                                                ?.value ??
                                                            notSelectedValue
                                                        }
                                                        onChange={(newValue) =>
                                                            updateFeedbackValueForCandidate(
                                                                candidate.candidatePersonId,
                                                                safeCastToEnum(
                                                                    newValue,
                                                                    FeedbackValues,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </Section>
                                                <Section
                                                    title="Comments"
                                                    titleType="h2"
                                                >
                                                    <textarea
                                                        placeholder="Comments"
                                                        rows={3}
                                                        cols={20}
                                                        aria-label="Comments"
                                                        className="form-input text-area-input min-w-[400px]"
                                                        onChange={(
                                                            event: React.ChangeEvent<HTMLTextAreaElement>,
                                                        ) =>
                                                            localUpdateFeedbackComment(
                                                                candidate.candidatePersonId,
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                        value={
                                                            hiringManagerConnection
                                                                ?.feedback
                                                                ?.comment ?? ''
                                                        }
                                                        onBlur={() =>
                                                            saveCommentChanges(
                                                                candidate.candidatePersonId,
                                                            )
                                                        }
                                                    ></textarea>
                                                </Section>
                                                {hiringManagerConnection
                                                    ?.feedback?.value !==
                                                    FeedbackValues.WILL_NOT_WORK_WITH_AGAIN &&
                                                    hiringManagerConnection
                                                        ?.feedback?.value !==
                                                        FeedbackValues.HIDDEN &&
                                                    hiringManagerConnection
                                                        ?.feedback?.value !==
                                                        undefined && (
                                                        <Section
                                                            title="Interview?"
                                                            titleType="h2"
                                                        >
                                                            <MultiStateSelector
                                                                options={
                                                                    structuredInterviewOptions
                                                                }
                                                                selectedValue={
                                                                    hiringManagerConnection?.interviewForThisRole ??
                                                                    notSelectedValue
                                                                }
                                                                onChange={(
                                                                    newValue,
                                                                ) =>
                                                                    handleInterviewChange(
                                                                        candidate.candidatePersonId,
                                                                        safeCastToEnum(
                                                                            newValue,
                                                                            InterviewForThisRoleValues,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                        </Section>
                                                    )}
                                            </div>
                                        </LoadingSpinnerOverlay>
                                    </div>
                                )}
                                <div className="flex">
                                    <RankingScore
                                        className="max-w-[120px]"
                                        label="Combined Score (Normalised)"
                                        value={candidate.rankingScore}
                                    />
                                    <RankingScore
                                        className="max-w-[120px]"
                                        label="Closeness Score (Normalised)"
                                        value={candidate.closenessScore}
                                    />
                                    <RankingScore
                                        className="max-w-[140px]"
                                        label="Job Relevance Score (Normalised)"
                                        value={candidate.jobRelevanceScore}
                                    />
                                    <RankingScore
                                        className="max-w-[120px]"
                                        label="Job Relevance Score (Raw)"
                                        value={candidate.jobRelevanceRawScore}
                                    />
                                    <div>
                                        <div className="field-label-allow-wrap">
                                            Job Title Used
                                        </div>
                                        {candidate.jobRelevanceJobTitle}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {connectionsToShow.map(
                            (connection, connectionIndex) => {
                                const connectionPerson =
                                    personLiteCache.getOrThrow(
                                        connection.connectedPersonId,
                                    )

                                return (
                                    <div
                                        key={`connection-${connectionIndex}`}
                                        className="flex flex-col mb-3 border border-gray-300 shadow-md rounded-md p-4 pb-0 w-full"
                                    >
                                        <div className="flex space-x-6 items-center mb-2 bg-gray-100 -m-4 p-3">
                                            <div>
                                                <PersonUi
                                                    person={connectionPerson}
                                                    showCurrentJobDetails={
                                                        false
                                                    }
                                                    imageClasses="w-[50px] h-[50px]"
                                                />
                                            </div>

                                            <div className="flex justify-between w-full">
                                                <div className="mt-3">
                                                    <div className="field-label">
                                                        Interview
                                                    </div>
                                                    <InterviewForThisRolePill
                                                        value={
                                                            connection.interviewForThisRole
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {connection.sharedWorkHistory?.length >
                                            0 && (
                                            <Section
                                                title="Shared Work History"
                                                titleType="h2"
                                                className=""
                                            >
                                                <div className="mt-2">
                                                    {connection.sharedWorkHistory.map(
                                                        (
                                                            item,
                                                            historyIndex,
                                                        ) => {
                                                            const overlapDuration =
                                                                formatDuration(
                                                                    item.startOfOverlap,
                                                                    item.endOfOverlap,
                                                                )

                                                            return (
                                                                <table
                                                                    key={`history-${historyIndex}`}
                                                                    className="w-full text-sm text-center"
                                                                >
                                                                    <colgroup>
                                                                        <col
                                                                            style={{
                                                                                width: '30%',
                                                                            }}
                                                                        />
                                                                        <col
                                                                            style={{
                                                                                width: '30%',
                                                                            }}
                                                                        />
                                                                        <col
                                                                            style={{
                                                                                width: '20%',
                                                                            }}
                                                                        />
                                                                        <col
                                                                            style={{
                                                                                width: '20%',
                                                                            }}
                                                                        />
                                                                    </colgroup>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="flex items-center">
                                                                                <div className="flex-shrink-0">
                                                                                    <PersonSmallPic
                                                                                        person={
                                                                                            connectionPerson
                                                                                        }
                                                                                        size="50px"
                                                                                    />
                                                                                </div>
                                                                                <div className="ml-2 text-left">
                                                                                    {
                                                                                        item
                                                                                            .seedPersonWorkedAt
                                                                                            .jobTitle
                                                                                    }
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <div className="flex items-center">
                                                                                    <div className="flex-shrink-0">
                                                                                        <PersonSmallPic
                                                                                            person={
                                                                                                candidatePerson
                                                                                            }
                                                                                            size="50px"
                                                                                        />
                                                                                    </div>
                                                                                    <div className="ml-2 break-words min-w-0 text-left">
                                                                                        {
                                                                                            item
                                                                                                .resolvedPersonWorkedAt
                                                                                                .jobTitle
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                {
                                                                                    companyCache.getOrThrow(
                                                                                        item.companyId,
                                                                                    )
                                                                                        .name
                                                                                }
                                                                            </td>
                                                                            <td className="text-left">
                                                                                <p className="text-sm text-gray-500 text-nowrap">
                                                                                    {
                                                                                        overlapDuration.fromTo
                                                                                    }
                                                                                </p>
                                                                                <p className="text-sm text-gray-500 text-nowrap">
                                                                                    {
                                                                                        overlapDuration.duration
                                                                                    }
                                                                                </p>
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            )
                                                        },
                                                    )}
                                                </div>
                                            </Section>
                                        )}
                                    </div>
                                )
                            },
                        )}
                        {candidate.companyConnections.map(
                            (companyConnection, companyIndex) => {
                                const company = companyCache.getOrThrow(
                                    companyConnection.connectedCompanyId,
                                )

                                return (
                                    <div
                                        key={`company-connection-${companyIndex}`}
                                        className="flex flex-col border border-gray-300 shadow-md rounded-md w-full mb-2"
                                    >
                                        <div className="flex space-x-6 items-center mb-2 bg-gray-100">
                                            <div className="m-4">
                                                <CompanyUi company={company} />
                                            </div>
                                        </div>
                                    </div>
                                )
                            },
                        )}
                        {candidate.connections.length > visibleConnections &&
                            (() => {
                                const hiddenCount =
                                    candidate.connections.length -
                                    visibleConnections
                                const loadCount = Math.min(10, hiddenCount)
                                return (
                                    <div className="text-center mt-2">
                                        <a
                                            className="cursor-pointer text-blue-500"
                                            onClick={() =>
                                                setVisibleConnectionsByCandidate(
                                                    (prev) => ({
                                                        ...prev,
                                                        [candidateId]:
                                                            visibleConnections +
                                                            loadCount,
                                                    }),
                                                )
                                            }
                                        >
                                            Show next {loadCount} connections...
                                            ({hiddenCount} hidden)
                                        </a>
                                    </div>
                                )
                            })()}
                    </div>
                )
            })}

            {!fetchingCandidates && hasMoreCandidates && (
                <div className="text-center mt-4">
                    <hr className="mb-4" />
                    <a
                        className="cursor-pointer text-blue-500"
                        onClick={showMoreClicked}
                    >
                        Show 50 more candidates...
                    </a>
                </div>
            )}
            {fetchingCandidates && candidates.length > 0 && (
                <div className="text-center mt-4">
                    <LoadingSpinner show={fetchingCandidates} />
                </div>
            )}
        </div>
    )
}

// using this weird memo thing to avoid re-renders
// e.g. when hovering over a candidate on the network view, then over a HM (or vice versa) was causing a re-render of this heavy list component
//  this memo stuff avoids that!
const CandidateList = memo(CandidateListComponent)
export default CandidateList
